import { OfficeLocation, AIAgent, PlacedFurniture, OfficeRoom } from '../types';
import { AGENT_ROLES_CONFIG } from './officeCatalog';

// Helper to generate warehouse floors
function generateWarehouseFloors() {
  const customFloors: Record<string, { tileType: any; roomName?: string }> = {};
  for (let c = 0; c < 16; c++) {
    for (let r = 0; r < 12; r++) {
      if (c < 7 && r < 7) {
        customFloors[`${c}_${r}`] = { tileType: 'grey_carpet', roomName: 'Stock & Packaging Zone' };
      } else if (c >= 10 && r < 6) {
        customFloors[`${c}_${r}`] = { tileType: 'blue_checkered', roomName: 'Data Center & Servers' };
      } else if (r >= 8 && c < 8) {
        customFloors[`${c}_${r}`] = { tileType: 'wood_parquet', roomName: 'Loading Dock & Shipping' };
      } else {
        customFloors[`${c}_${r}`] = { tileType: 'grey_carpet', roomName: 'Transit Area' };
      }
    }
  }
  return customFloors;
}

// Helper to generate Floor 2 floors
function generatePlanta2Floors() {
  const customFloors: Record<string, { tileType: any; roomName?: string }> = {};
  for (let c = 0; c < 12; c++) {
    for (let r = 0; r < 12; r++) {
      if (c < 6 && r < 6) {
        customFloors[`${c}_${r}`] = { tileType: 'wood_parquet', roomName: 'Executive Office' };
      } else if (c >= 6 && r < 6) {
        customFloors[`${c}_${r}`] = { tileType: 'blue_checkered', roomName: 'Creative Studio & Brainstorming' };
      } else {
        customFloors[`${c}_${r}`] = { tileType: 'grey_carpet', roomName: 'Panoramic Lounge & Cafe' };
      }
    }
  }
  return customFloors;
}

// Default initial state: ONLY 1 floor, completely clean and empty grid with 4 available agents (unplaced on grid)
export const DEFAULT_STARTUP_AGENTS: AIAgent[] = [
  {
    id: 'agent_devops_default',
    name: 'Leo DevOps',
    nickname: 'Leo DevOps',
    role: 'devops',
    avatarShirtColor: '#3b82f6',
    hairColor: '#451a03',
    assignedDeskId: null,
    isPlaced: false,
    currentCol: -1,
    currentRow: -1,
    renderX: 0,
    renderY: 0,
    targetCol: null,
    targetRow: null,
    path: [],
    status: 'idle',
    statusTimer: 15,
    energy: 90,
    bladder: 20,
    focus: 85,
    taskQueue: [],
    processedTasksCount: 15,
    avgResponseTimeSec: 1.5,
    systemPrompt: AGENT_ROLES_CONFIG.devops?.defaultPrompt || 'Expert in CI/CD pipelines, cloud infrastructure, and continuous delivery.',
    thoughts: [{ time: 'Now', text: 'Available in the agent roster, ready to be placed on the grid.', mood: 'happy' }],
  },
  {
    id: 'agent_front_default',
    name: 'Ana Frontend',
    nickname: 'Ana Frontend',
    role: 'frontend',
    avatarShirtColor: '#f97316',
    hairColor: '#78350f',
    assignedDeskId: null,
    isPlaced: false,
    currentCol: -1,
    currentRow: -1,
    renderX: 0,
    renderY: 0,
    targetCol: null,
    targetRow: null,
    path: [],
    status: 'idle',
    statusTimer: 15,
    energy: 85,
    bladder: 30,
    focus: 90,
    taskQueue: [],
    processedTasksCount: 20,
    avgResponseTimeSec: 1.8,
    systemPrompt: AGENT_ROLES_CONFIG.frontend?.defaultPrompt || 'Specialist in interactive user interfaces, animation, and accessibility.',
    thoughts: [{ time: 'Now', text: 'Ready to join as soon as you place me on the floor plan.', mood: 'happy' }],
  },
  {
    id: 'agent_back_default',
    name: 'Carlos Backend',
    nickname: 'Carlos Backend',
    role: 'backend',
    avatarShirtColor: '#10b981',
    hairColor: '#1e293b',
    assignedDeskId: null,
    isPlaced: false,
    currentCol: -1,
    currentRow: -1,
    renderX: 0,
    renderY: 0,
    targetCol: null,
    targetRow: null,
    path: [],
    status: 'idle',
    statusTimer: 15,
    energy: 80,
    bladder: 25,
    focus: 80,
    taskQueue: [],
    processedTasksCount: 18,
    avgResponseTimeSec: 2.1,
    systemPrompt: AGENT_ROLES_CONFIG.backend?.defaultPrompt || 'Architect for distributed systems, robust APIs, and databases.',
    thoughts: [{ time: 'Now', text: 'Prepared in the pool to start once assigned a desk.', mood: 'busy' }],
  },
  {
    id: 'agent_qa_default',
    name: 'Sara QA',
    nickname: 'Sara QA',
    role: 'qa',
    avatarShirtColor: '#8b5cf6',
    hairColor: '#b45309',
    assignedDeskId: null,
    isPlaced: false,
    currentCol: -1,
    currentRow: -1,
    renderX: 0,
    renderY: 0,
    targetCol: null,
    targetRow: null,
    path: [],
    status: 'idle',
    statusTimer: 18,
    energy: 95,
    bladder: 15,
    focus: 95,
    taskQueue: [],
    processedTasksCount: 25,
    avgResponseTimeSec: 1.4,
    systemPrompt: AGENT_ROLES_CONFIG.qa?.defaultPrompt || 'Quality assurance engineer and automated test suite specialist.',
    thoughts: [{ time: 'Now', text: 'Available to deploy onto the board.', mood: 'happy' }],
  },
];

export const DEFAULT_LOCATIONS: OfficeLocation[] = [
  {
    id: 'loc_planta_1',
    name: 'Main Floor',
    icon: '🏢',
    locationType: 'office',
    description: 'Open, clean main floor ready for layout design and simulation.',
    gridWidth: 20,
    gridHeight: 20,
    floorType: 'blue_checkered',
    customFloors: {},
    rooms: [],
    items: [],
    agents: DEFAULT_STARTUP_AGENTS,
  },
];

// Optional templates if the user wants to load preset rooms or full demo setups later from Location Manager
export const PRESET_LOCATION_TEMPLATES: OfficeLocation[] = [
  {
    id: 'loc_template_office',
    name: 'Office Floor (Demo)',
    icon: '🏢',
    locationType: 'office',
    description: 'Main headquarters with workstations, meeting room, and cafeteria.',
    gridWidth: 14,
    gridHeight: 14,
    floorType: 'blue_checkered',
    rooms: [
      {
        id: 'room_meeting_p1',
        name: 'Meeting & Ideas Room',
        type: 'meeting',
        color: '#8b5cf6',
        icon: '📊',
        col: 0,
        row: 0,
        width: 5,
        height: 4,
        policy: {
          energyDelta: -0.1,
          focusBonus: 20,
          bladderDelta: 0.2,
          maxCapacity: 6,
          ambientStatusText: 'In team strategic meeting',
        },
        description: 'Enclosed space with conference table and brainstorming whiteboard.',
      },
      {
        id: 'room_dev_p1',
        name: 'Development & Engineering Area',
        type: 'work',
        color: '#3b82f6',
        icon: '💻',
        col: 5,
        row: 1,
        width: 8,
        height: 7,
        policy: {
          energyDelta: -0.3,
          focusBonus: 15,
          bladderDelta: 0.4,
          maxCapacity: 10,
          ambientStatusText: 'Coding at workstation',
        },
        description: 'High-speed connected engineering workstations.',
      },
      {
        id: 'room_cafeteria_p1',
        name: 'Cafeteria & Breakroom',
        type: 'breakroom',
        color: '#f59e0b',
        icon: '☕',
        col: 8,
        row: 9,
        width: 6,
        height: 5,
        policy: {
          energyDelta: 3.5,
          focusBonus: 5,
          bladderDelta: 0.8,
          maxCapacity: 8,
          ambientStatusText: 'Grabbing coffee / taking a break',
        },
        description: 'Caffeine recharge station, snacks, and lounge seating.',
      },
      {
        id: 'room_restroom_p1',
        name: 'Restrooms & Hygiene',
        type: 'restroom',
        color: '#06b6d4',
        icon: '🚽',
        col: 0,
        row: 9,
        width: 4,
        height: 5,
        policy: {
          energyDelta: 0.5,
          focusBonus: 0,
          bladderDelta: -8.0,
          maxCapacity: 3,
          ambientStatusText: 'In restroom',
        },
        description: 'Sanitary facilities and personal hygiene.',
      },
    ],
    items: [
      { id: 'w1', type: 'wall_brick', col: 0, row: 0, rotation: 0 },
      { id: 'w2', type: 'wall_brick', col: 0, row: 1, rotation: 0 },
      { id: 'w3', type: 'wall_brick', col: 0, row: 2, rotation: 0 },
      { id: 'w4', type: 'wall_brick', col: 0, row: 3, rotation: 0 },
      { id: 'g1', type: 'wall_glass', col: 4, row: 0, rotation: 0 },
      { id: 'g2', type: 'wall_glass', col: 4, row: 1, rotation: 0 },
      { id: 'g3', type: 'wall_glass', col: 4, row: 2, rotation: 0 },
      { id: 'd1', type: 'door_single', col: 4, row: 3, rotation: 0 },
      { id: 'm_table', type: 'meeting_table', col: 1, row: 1, rotation: 0 },
      { id: 'wb1', type: 'whiteboard', col: 1, row: 3, rotation: 0, label: 'Sprint Board' },
      { id: 'tv1', type: 'tv_screen', col: 3, row: 0, rotation: 0 },
      { id: 'desk_devops', type: 'desk_single', col: 6, row: 2, rotation: 0, label: 'DevOps Desk', assignedAgentId: 'agent_devops' },
      { id: 'desk_front', type: 'desk_single', col: 8, row: 2, rotation: 0, label: 'Frontend Desk', assignedAgentId: 'agent_front' },
      { id: 'desk_back', type: 'desk_single', col: 6, row: 5, rotation: 0, label: 'Backend Desk', assignedAgentId: 'agent_back' },
      { id: 'desk_qa', type: 'desk_single', col: 8, row: 5, rotation: 0, label: 'QA Desk', assignedAgentId: 'agent_qa' },
      { id: 'desk_mgr', type: 'executive_desk', col: 11, row: 2, rotation: 0, label: 'Lead Desk', assignedAgentId: 'agent_mgr' },
      { id: 'desk_supp', type: 'desk_single', col: 11, row: 5, rotation: 0, label: 'Support Desk', assignedAgentId: 'agent_supp' },
      { id: 'coffee1', type: 'coffee_station', col: 11, row: 9, rotation: 0, label: 'Espresso Bar' },
      { id: 'water1', type: 'water_dispenser', col: 13, row: 9, rotation: 0, label: 'Fresh Water' },
      { id: 'fridge1', type: 'fridge', col: 12, row: 9, rotation: 0 },
      { id: 'snack_t', type: 'snack_table', col: 11, row: 11, rotation: 0 },
      { id: 'sofa1', type: 'sofa', col: 8, row: 11, rotation: 0 },
      { id: 'toilet1', type: 'toilet', col: 0, row: 11, rotation: 0, label: 'WC 1' },
      { id: 'toilet2', type: 'toilet', col: 2, row: 11, rotation: 0, label: 'WC 2' },
      { id: 'sink1', type: 'sink', col: 1, row: 13, rotation: 0 },
      { id: 'print1', type: 'printer_large', col: 1, row: 6, rotation: 0 },
      { id: 'cab1', type: 'filing_cabinet', col: 0, row: 6, rotation: 0 },
      { id: 'p1', type: 'plant_monstera', col: 5, row: 2, rotation: 0 },
      { id: 'p2', type: 'plant_monstera', col: 10, row: 2, rotation: 0 },
    ],
    agents: [
      {
        id: 'agent_devops',
        name: 'Alex DevOps',
        role: 'devops',
        avatarShirtColor: '#ef4444',
        hairColor: '#451a03',
        assignedDeskId: 'desk_devops',
        currentCol: 6,
        currentRow: 3,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 12,
        energy: 85,
        bladder: 25,
        focus: 90,
        taskQueue: [],
        processedTasksCount: 14,
        avgResponseTimeSec: 2.1,
        systemPrompt: AGENT_ROLES_CONFIG.devops.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Stable deployments on Kubernetes.', mood: 'happy' }],
      },
      {
        id: 'agent_front',
        name: 'Elena UI',
        role: 'frontend',
        avatarShirtColor: '#3b82f6',
        hairColor: '#f59e0b',
        assignedDeskId: 'desk_front',
        currentCol: 8,
        currentRow: 3,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 15,
        energy: 70,
        bladder: 40,
        focus: 85,
        taskQueue: [],
        processedTasksCount: 22,
        avgResponseTimeSec: 1.8,
        systemPrompt: AGENT_ROLES_CONFIG.frontend.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Optimizing interface design and animations.', mood: 'busy' }],
      },
      {
        id: 'agent_back',
        name: 'Carlos Backend',
        role: 'backend',
        avatarShirtColor: '#10b981',
        hairColor: '#1e293b',
        assignedDeskId: 'desk_back',
        currentCol: 6,
        currentRow: 6,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 10,
        energy: 60,
        bladder: 65,
        focus: 80,
        taskQueue: [],
        processedTasksCount: 19,
        avgResponseTimeSec: 2.4,
        systemPrompt: AGENT_ROLES_CONFIG.backend.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Refactoring APIs and microservices.', mood: 'busy' }],
      },
      {
        id: 'agent_qa',
        name: 'Sara QA',
        role: 'qa',
        avatarShirtColor: '#8b5cf6',
        hairColor: '#b45309',
        assignedDeskId: 'desk_qa',
        currentCol: 8,
        currentRow: 6,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 18,
        energy: 90,
        bladder: 15,
        focus: 95,
        taskQueue: [],
        processedTasksCount: 31,
        avgResponseTimeSec: 1.5,
        systemPrompt: AGENT_ROLES_CONFIG.qa.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Zero bugs detected in the latest test suite.', mood: 'happy' }],
      },
      {
        id: 'agent_supp',
        name: 'Hugo Support',
        role: 'support',
        avatarShirtColor: '#ec4899',
        hairColor: '#0284c7',
        assignedDeskId: 'desk_supp',
        currentCol: 11,
        currentRow: 6,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 8,
        energy: 40,
        bladder: 80,
        focus: 60,
        taskQueue: [],
        processedTasksCount: 45,
        avgResponseTimeSec: 1.2,
        systemPrompt: AGENT_ROLES_CONFIG.support.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Answering customer inquiries.', mood: 'tired' }],
      },
      {
        id: 'agent_mgr',
        name: 'Diana Lead',
        role: 'manager',
        avatarShirtColor: '#6366f1',
        hairColor: '#475569',
        assignedDeskId: 'desk_mgr',
        currentCol: 11,
        currentRow: 3,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 20,
        energy: 95,
        bladder: 20,
        focus: 90,
        taskQueue: [],
        processedTasksCount: 12,
        avgResponseTimeSec: 3.0,
        systemPrompt: AGENT_ROLES_CONFIG.manager.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Planning the roadmap for the next Sprint.', mood: 'happy' }],
      },
    ],
  },
  {
    id: 'loc_template_warehouse',
    name: 'Warehouse & Logistics (Demo)',
    icon: '📦',
    locationType: 'warehouse',
    description: 'Industrial open warehouse for inventory, packaging, servers, and loading docks.',
    gridWidth: 16,
    gridHeight: 12,
    floorType: 'grey_carpet',
    customFloors: generateWarehouseFloors(),
    rooms: [
      {
        id: 'room_warehouse_stock',
        name: 'Stock & Packaging Zone',
        type: 'custom',
        color: '#f59e0b',
        icon: '📦',
        col: 0,
        row: 0,
        width: 7,
        height: 7,
        policy: {
          energyDelta: -0.2,
          focusBonus: 10,
          bladderDelta: 0.3,
          maxCapacity: 6,
          ambientStatusText: 'Taking inventory and packaging orders',
        },
        description: 'Storage for boxes, pallets, and product inventory.',
      },
      {
        id: 'room_servers',
        name: 'Data Center & Servers',
        type: 'server_room',
        color: '#06b6d4',
        icon: '🖥️',
        col: 10,
        row: 0,
        width: 6,
        height: 6,
        policy: {
          energyDelta: -0.4,
          focusBonus: 30,
          bladderDelta: 0.2,
          maxCapacity: 3,
          allowedRoles: ['devops', 'backend'],
          ambientStatusText: 'Monitoring server racks and temperatures',
        },
        description: 'Cooled server racks with high-speed fiber optic connection.',
      },
    ],
    items: [
      { id: 'wh_cab1', type: 'filing_cabinet', col: 1, row: 1, rotation: 0 },
      { id: 'wh_cab2', type: 'filing_cabinet', col: 2, row: 1, rotation: 0 },
      { id: 'wh_desk1', type: 'desk_single', col: 3, row: 3, rotation: 0, label: 'Stock Control' },
      { id: 'wh_desk2', type: 'desk_single', col: 5, row: 3, rotation: 0, label: 'Shipments' },
      { id: 'wh_water', type: 'water_dispenser', col: 0, row: 6, rotation: 0 },
      { id: 'wh_coffee', type: 'coffee_station', col: 1, row: 6, rotation: 0 },
      { id: 'wh_sofa', type: 'sofa', col: 3, row: 9, rotation: 0 },
      { id: 'wh_print', type: 'printer_large', col: 14, row: 8, rotation: 0 },
    ],
    agents: [],
  },
  {
    id: 'loc_template_lounge',
    name: 'Panoramic Lounge & Executive Suites (Demo)',
    icon: '🛋️',
    locationType: 'lounge',
    description: 'Top floor with executive suites, creative ideation studios, and lounge cafe.',
    gridWidth: 12,
    gridHeight: 12,
    floorType: 'wood_parquet',
    customFloors: generatePlanta2Floors(),
    rooms: [
      {
        id: 'room_exec_p2',
        name: 'Executive Office',
        type: 'work',
        color: '#6366f1',
        icon: '💼',
        col: 0,
        row: 0,
        width: 6,
        height: 6,
        policy: {
          energyDelta: -0.1,
          focusBonus: 30,
          bladderDelta: 0.3,
          maxCapacity: 4,
          allowedRoles: ['manager'],
          ambientStatusText: 'In executive decision making',
        },
        description: 'Executive workspace with director desk and reference library.',
      },
      {
        id: 'room_creative_p2',
        name: 'Creative Studio & Brainstorming',
        type: 'meeting',
        color: '#ec4899',
        icon: '💡',
        col: 6,
        row: 0,
        width: 6,
        height: 6,
        policy: {
          energyDelta: 0.1,
          focusBonus: 25,
          bladderDelta: 0.3,
          maxCapacity: 6,
          ambientStatusText: 'Designing visual concepts and wireframes',
        },
        description: 'Creative ideation hub with whiteboards and collaboration tables.',
      },
    ],
    items: [
      { id: 'p2_desk_dir', type: 'executive_desk', col: 2, row: 2, rotation: 0, label: 'CEO Desk' },
      { id: 'p2_creative_t', type: 'meeting_table', col: 8, row: 2, rotation: 0, label: 'Design Sprint Table' },
      { id: 'p2_sofa1', type: 'sofa', col: 2, row: 8, rotation: 0 },
      { id: 'p2_coffee', type: 'coffee_station', col: 10, row: 8, rotation: 0, label: 'Coffee Corner' },
    ],
    agents: [],
  },
];
